// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import useSummaryStore from '@store/summaryStore';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { StatCardsSkeleton, TableSkeleton } from '@/components/Page/PageSkeletons';
import PaymentsList from './PaymentsList';

// Icons
import {
  DollarSign,
  Receipt,
  Percent,
  RefreshCw
} from 'lucide-react';

const formatCurrency = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const TransactionAdmin = () => {
  const { getToken } = useAuth();
  const { league } = useOutletContext() as any;

  const { summary, getSummary } = useSummaryStore((state: any) => ({
    summary: state.summary,
    getSummary: state.getSummary(getToken),
  }));

  // Skeleton only until the FIRST fetch resolves — and only when the store has
  // nothing cached (the cards used to render "$0.00" / "—" while fetching).
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (league?._id) Promise.resolve(getSummary(league._id)).finally(() => setLoaded(true));
  }, [league?._id]);

  const showSkeleton = !loaded && Object.keys(summary || {}).length === 0;

  const summaryCards = [
    {
      label: 'Total Revenue',
      value: formatCurrency(summary?.totalRevenue),
      hint: 'Completed, non-refunded payments',
      Icon: DollarSign,
    },
    {
      label: 'Platform Fees',
      value: formatCurrency(summary?.totalPlatformFees),
      hint: "Staty's share (1% + 30¢ per payment) of the revenue above",
      Icon: Percent,
    },
    {
      label: 'Payments',
      value: summary?.totalPayments ?? '—',
      hint: 'All payment records for this organization',
      Icon: Receipt,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Transactions"
        description="Monitor and manage all payment transactions and donations"
        actions={(
          <Button size="lg" onClick={() => league?._id && getSummary(league._id)}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        )}
      />

      {/* Summary strip — fed by GET /summary/dashboard/:leagueId */}
      {showSkeleton ? (
        <StatCardsSkeleton count={3} className="grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-3" />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {summaryCards.map(({ label, value, hint, Icon }) => (
            <Card key={label} className="shadow-sm border-0">
              <CardContent className="flex items-center justify-between pt-6">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold text-foreground">{value}</p>
                  <p className="text-xs text-muted-foreground/60">{hint}</p>
                </div>
                <Icon className="h-8 w-8 text-muted-foreground/60" aria-hidden="true" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Original PaymentsList Component */}
      {showSkeleton ? (
        <TableSkeleton />
      ) : (
        <Card className="shadow-sm border-0 pt-4">
          <CardHeader>
            <CardTitle>Detailed Transaction List</CardTitle>
            <CardDescription>Advanced transaction management and filtering</CardDescription>
          </CardHeader>
          <CardContent>
            <PaymentsList />
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
};

export default TransactionAdmin;
